import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "depth", "$type"];
import { CALYPSO_LIGHT, CALYPSO, EERIE, FLAT_EARTH, GYPSUM, OBSIDIAN } from 'HubStyleTokens/colors';
import styled from 'styled-components';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import themes from 'developer-docs-shared-components/config/themes';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import { useViewport } from 'developer-docs-shared-components/hooks/useViewport';
import { ExpandableHeader, ExpandableOpener, ExpandableCaret } from '../ExpandableComponents';
import { TOC_CLICK } from '../../tracking/trackerActions';
import { getUsageTracker } from '../../utils/usage-tracker/index';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/**
 * At mobile, the header for each expandable section.
 */
const Header = styled(ExpandableHeader).withConfig({
  displayName: "ListItem__Header",
  componentId: "sc-18jp0iz-0"
})(["color:", ";margin:9px 0;padding:4px 9px;"], OBSIDIAN);

/**
 * An unordered list within the table of contents.
 */
export const TocUL = styled.ul.withConfig({
  displayName: "ListItem__TocUL",
  componentId: "sc-18jp0iz-1"
})(["list-style:none;margin-top:16px;padding:0;"]);

/**
 * Table of contents link.
 */
const Link = styled(_ref => {
  let rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(UILink, Object.assign({}, rest));
}).withConfig({
  displayName: "ListItem__Link",
  componentId: "sc-18jp0iz-2"
})(["display:block;font-weight:", ";padding:4px 8px;color:", ";line-height:1.85;", "{&:hover{color:", ";text-decoration:none;background-color:", ";}}", "{margin-bottom:24px;}", "{border-left:3px solid ", ";background-color:", ";}"], ({
  depth
}) => depth > 0 ? '400' : '500', FLAT_EARTH, mediaQueries.hover, FLAT_EARTH, ({
  $type,
  theme: {
    themeName = ''
  }
} = {}) => {
  if (themeName === themes.PUBLIC.themeName) {
    return `${$type !== 'category' ? CALYPSO_LIGHT : null};`;
  }
  return GYPSUM;
}, mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT), mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), ({
  active,
  $type,
  theme: {
    themeName = ''
  }
} = {}) => {
  if (themeName === themes.PUBLIC.themeName) {
    return `${$type !== 'category' && active ? CALYPSO : 'transparent'};`;
  }
  return $type !== 'category' && active ? GYPSUM : 'transparent';
}, ({
  active,
  $type,
  theme: {
    themeName = ''
  }
} = {}) => {
  if (themeName === themes.PUBLIC.themeName) {
    return `${$type !== 'category' && active ? CALYPSO_LIGHT : 'transparent'};`;
  }
  return $type !== 'category' && active ? GYPSUM : 'transparent';
});

/**
 * A table of contents list item.
 */
const LI = styled.li.withConfig({
  displayName: "ListItem__LI",
  componentId: "sc-18jp0iz-3"
})(["display:", ";padding:", ";", "{margin:0 0 16px 0;}"], ({
  visible
}) => visible ? 'block' : 'none', ({
  $type
}) => $type === 'item' ? '0' : '2px 0 0 0', mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));

/**
 * A `li`-wrapped link with a submenu `ul`, if applicable. Renders as
 * expandable sections at mobile and onScroll-rendered submenus at desktop.
 */
const ListItem = ({
  activeToc,
  activeChildToc,
  item: {
    depth,
    id,
    items = [],
    label,
    parent
  }
}) => {
  const viewportContext = useViewport();
  const uiType = !viewportContext || viewportContext && viewportContext.isDesktop ? 'list' : 'accordion';
  const isCategory = items.length > 0;
  const itemType = isCategory ? 'category' : 'item';
  const passthroughProps = {
    activeToc,
    activeChildToc
  };
  const trackEvent = tocLink => {
    const pathname = window.location.pathname.split('/');
    getUsageTracker().track(TOC_CLICK, {
      page: pathname[pathname.length - 1],
      tocLink
    });
  };

  /**
   * The nested unordered list containing items in a category.
   */
  const subMenuEl = /*#__PURE__*/_jsx(TocUL, {
    children: items.map(item => /*#__PURE__*/_jsx(ListItem, Object.assign({
      item: item
    }, passthroughProps), item.id))
  });

  /**
   * An item's link element.
   */
  const linkEl = /*#__PURE__*/_jsxs(Link, {
    active: activeToc === id || activeChildToc === id,
    depth: depth,
    $type: itemType,
    onClick: () => {
      trackEvent(id);
    },
    href: `#${id}`,
    children: [label, isCategory && /*#__PURE__*/_jsx(UIIcon, {
      name: "downCarat",
      color: EERIE,
      size: 12,
      className: "m-left-3 display-inline"
    })]
  });
  return /*#__PURE__*/_jsxs(LI, {
    depth: depth,
    $type: itemType,
    parent: parent,
    visible: uiType === 'list' ? parent === null || parent === activeToc : true,
    children: [uiType === 'list' && /*#__PURE__*/_jsxs(_Fragment, {
      children: [linkEl, isCategory && subMenuEl]
    }), uiType === 'accordion' && /*#__PURE__*/_jsx(_Fragment, {
      children: isCategory ? /*#__PURE__*/_jsx(UIExpandableSection, {
        contentClassName: "p-bottom-0",
        indentation: "none",
        titleText: label,
        Header: !viewportContext || viewportContext.isDesktop ? undefined : Header,
        Opener: !viewportContext || viewportContext.isDesktop ? undefined : ExpandableOpener,
        Caret: !viewportContext || viewportContext.isDesktop ? undefined : ExpandableCaret,
        children: subMenuEl
      }) : linkEl
    })]
  }, id);
};
export default ListItem;